import { Button, Callout, Card, Input, LoadingRegion, Skeleton, useToast } from '@apovisio/ui';
import { totpCodeSchema } from '@apovisio/contracts';
import type { TotpEnrollmentResponse } from '@apovisio/contracts';
import { useQueryClient } from '@tanstack/react-query';
import { Copy, Download, KeyRound, Smartphone } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import type { SyntheticEvent } from 'react';
import { Navigate, useNavigate } from 'react-router';
import { BrandMark } from '../../app/BrandMark';
import { PageHeading } from '../../components/PageHeading';
import { QrCode } from '../../components/QrCode';
import {
  ME_QUERY_KEY,
  useConfirmEnrollment,
  useMe,
  useRecoveryCode,
  useStartEnrollment,
  useVerifyTotp,
} from '../../features/auth/api';
import { ApiClientError } from '../../lib/api';

function errorMessage(error: unknown): string | undefined {
  if (!error) return undefined;
  return error instanceof ApiClientError ? error.message : 'Une erreur inattendue est survenue.';
}

/** Valide la saisie localement avant l'envoi : 6 chiffres. */
function useCodeField() {
  const [code, setCode] = useState('');
  const [localError, setLocalError] = useState<string | undefined>();
  const validate = (): string | null => {
    const parsed = totpCodeSchema.safeParse(code);
    if (!parsed.success) {
      setLocalError(parsed.error.issues[0]?.message);
      return null;
    }
    setLocalError(undefined);
    return parsed.data;
  };
  return { code, setCode, localError, validate };
}

/** Écran 2 — Vérification TOTP, avec enrôlement au premier accès. */
export function TotpPage() {
  const me = useMe();
  if (me.isPending) {
    return (
      <main className="app-centered">
        <LoadingRegion label="Vérification de la session">
          <Skeleton width={280} height={20} />
        </LoadingRegion>
      </main>
    );
  }
  if (!me.data) return <Navigate to="/admin/connexion" replace />;
  return (
    <main className="app-centered">
      <Card padding="lg" className="app-narrow" as="div">
        <BrandMark subtitle="Espace Apogée" />
        {me.data.totp.enrolled ? (
          me.data.totp.verified ? (
            <Navigate to="/admin" replace />
          ) : (
            <VerifyStep />
          )
        ) : (
          <EnrollmentFlow />
        )}
      </Card>
    </main>
  );
}

function EnrollmentFlow() {
  const start = useStartEnrollment();
  const started = useRef(false);
  const [enrollment, setEnrollment] = useState<TotpEnrollmentResponse | null>(null);
  const [recoveryCodes, setRecoveryCodes] = useState<string[] | null>(null);

  useEffect(() => {
    // Un seul secret par affichage (StrictMode rejoue les effets en développement).
    if (started.current) return;
    started.current = true;
    start.mutate(undefined, { onSuccess: setEnrollment });
  }, [start]);

  if (recoveryCodes) return <RecoveryCodesStep codes={recoveryCodes} />;
  return (
    <>
      <div className="av-stack av-stack--sm">
        <p className="av-text-secondary av-text-sm">Étape 1 sur 2</p>
        <PageHeading documentTitle="Activer la vérification en deux étapes">
          Activer la vérification en deux étapes
        </PageHeading>
        <p className="av-text-secondary">
          Scannez ce QR code avec votre application d’authentification, puis saisissez le code
          affiché.
        </p>
      </div>
      {start.isError ? (
        <Callout tone="danger" title={errorMessage(start.error) ?? ''} live="alert" />
      ) : null}
      {enrollment ? (
        <>
          <div className="app-qr-frame">
            <QrCode value={enrollment.otpauthUri} label="QR code de configuration TOTP" />
          </div>
          <details>
            <summary>Saisir la clé manuellement</summary>
            <p className="app-mono">{enrollment.secret.replace(/(.{4})/g, '$1 ').trim()}</p>
          </details>
          <ConfirmEnrollmentForm onConfirmed={setRecoveryCodes} />
        </>
      ) : start.isPending ? (
        <LoadingRegion label="Génération de votre clé">
          <Skeleton width={200} height={200} />
        </LoadingRegion>
      ) : null}
    </>
  );
}

function ConfirmEnrollmentForm({ onConfirmed }: { onConfirmed: (codes: string[]) => void }) {
  const confirm = useConfirmEnrollment();
  const field = useCodeField();
  const submit = (event: SyntheticEvent<HTMLFormElement>) => {
    event.preventDefault();
    const code = field.validate();
    if (code)
      confirm.mutate(code, {
        onSuccess: (data) => {
          onConfirmed(data.recoveryCodes);
        },
      });
  };
  return (
    <form className="av-stack" onSubmit={submit} noValidate>
      <Input
        label="Code à 6 chiffres"
        variant="code"
        inputMode="numeric"
        autoComplete="one-time-code"
        maxLength={7}
        value={field.code}
        onChange={(event) => {
          field.setCode(event.target.value);
        }}
        error={field.localError ?? errorMessage(confirm.error)}
      />
      <Button type="submit" size="lg" fullWidth loading={confirm.isPending}>
        Vérifier et activer
      </Button>
    </form>
  );
}

function RecoveryCodesStep({ codes }: { codes: string[] }) {
  const toast = useToast();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [saved, setSaved] = useState(false);
  const text = `Apogée Visio — codes de récupération (usage unique)\n\n${codes.join('\n')}\n`;

  return (
    <>
      <div className="av-stack av-stack--sm">
        <p className="av-text-secondary av-text-sm">Étape 2 sur 2</p>
        <PageHeading documentTitle="Codes de récupération">Codes de récupération</PageHeading>
        <p className="av-text-secondary">
          Chaque code fonctionne une seule fois si vous perdez votre téléphone. Ils ne seront plus
          affichés.
        </p>
      </div>
      <ul className="app-codes" aria-label="Codes de récupération">
        {codes.map((code) => (
          <li key={code}>{code}</li>
        ))}
      </ul>
      <div className="av-row">
        <Button
          variant="secondary"
          icon={<Copy />}
          onClick={() => {
            navigator.clipboard.writeText(text).then(
              () => toast.show({ tone: 'success', title: 'Codes copiés' }),
              () =>
                toast.show({
                  tone: 'danger',
                  title: 'Copie impossible',
                  description: 'Sélectionnez les codes puis copiez-les manuellement.',
                }),
            );
          }}
        >
          Copier
        </Button>
        <Button
          variant="secondary"
          icon={<Download />}
          onClick={() => {
            const url = URL.createObjectURL(new Blob([text], { type: 'text/plain' }));
            const link = document.createElement('a');
            link.href = url;
            link.download = 'apogee-visio-codes-de-recuperation.txt';
            link.click();
            URL.revokeObjectURL(url);
          }}
        >
          Télécharger (.txt)
        </Button>
      </div>
      <label className="app-checkbox">
        <input
          type="checkbox"
          checked={saved}
          onChange={(event) => {
            setSaved(event.target.checked);
          }}
        />
        J’ai conservé ces codes en lieu sûr.
      </label>
      <Button
        size="lg"
        fullWidth
        disabled={!saved}
        onClick={() => {
          void queryClient
            .invalidateQueries({ queryKey: ME_QUERY_KEY })
            .then(() => navigate('/admin'));
        }}
      >
        Accéder à l’espace Apogée
      </Button>
    </>
  );
}

function VerifyStep() {
  const [mode, setMode] = useState<'totp' | 'recovery'>('totp');
  const navigate = useNavigate();
  const verify = useVerifyTotp();
  const recovery = useRecoveryCode();
  const field = useCodeField();
  const [recoveryCode, setRecoveryCode] = useState('');
  const pending = verify.isPending || recovery.isPending;
  const error = mode === 'totp' ? verify.error : recovery.error;
  const locked = error instanceof ApiClientError && error.errorCode === 'RATE_LIMITED';

  const submit = (event: SyntheticEvent<HTMLFormElement>) => {
    event.preventDefault();
    const onSuccess = () => {
      void navigate('/admin');
    };
    if (mode === 'totp') {
      const code = field.validate();
      if (code) verify.mutate(code, { onSuccess });
    } else {
      recovery.mutate(recoveryCode, { onSuccess });
    }
  };

  return (
    <>
      <div className="av-stack av-stack--sm">
        <PageHeading documentTitle="Vérification">
          {mode === 'totp' ? 'Vérification' : 'Code de récupération'}
        </PageHeading>
        <p className="av-text-secondary">
          {mode === 'totp'
            ? 'Saisissez le code affiché par votre application d’authentification.'
            : 'Saisissez l’un de vos codes de récupération. Il sera ensuite invalidé.'}
        </p>
      </div>
      {locked ? <Callout tone="warning" title={errorMessage(error) ?? ''} live="alert" /> : null}
      <form className="av-stack" onSubmit={submit} noValidate>
        {mode === 'totp' ? (
          <Input
            key="totp"
            label="Code à 6 chiffres"
            variant="code"
            inputMode="numeric"
            autoComplete="one-time-code"
            maxLength={7}
            value={field.code}
            onChange={(event) => {
              field.setCode(event.target.value);
            }}
            error={field.localError ?? (locked ? undefined : errorMessage(error))}
          />
        ) : (
          <Input
            key="recovery"
            label="Code de récupération"
            autoComplete="off"
            autoCapitalize="characters"
            value={recoveryCode}
            onChange={(event) => {
              setRecoveryCode(event.target.value);
            }}
            error={locked ? undefined : errorMessage(error)}
          />
        )}
        <Button type="submit" size="lg" fullWidth loading={pending} disabled={locked}>
          Valider
        </Button>
      </form>
      <Button
        variant="ghost"
        icon={mode === 'totp' ? <KeyRound /> : <Smartphone />}
        onClick={() => {
          setMode(mode === 'totp' ? 'recovery' : 'totp');
          verify.reset();
          recovery.reset();
        }}
      >
        {mode === 'totp' ? 'Utiliser un code de récupération' : 'Utiliser l’application'}
      </Button>
    </>
  );
}
